import React, { useMemo, useRef, useEffect } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useAppointmentStatuses } from "../../hooks/useAppointmentStatuses";

/**
 * Alternating-side timeline. Mirrors the infographic style:
 *   - Horizontal line through the middle.
 *   - Markers (dots) sit on the line.
 *   - Each entry's date + snippet appears above OR below in alternating fashion.
 *   - Click a marker to select that session.
 *
 * Long histories scroll horizontally (overflow-x auto). Selected marker is
 * coloured primary; unselected markers are muted; cancelled / no-show
 * entries get a strikethrough on their label.
 */

const ENTRY_MIN_WIDTH = 170; // px
const TIMELINE_HEIGHT = 280; // px

function formatDate(d) {
  if (!d) return "Unknown";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(d));
  } catch (_e) {
    return "Unknown";
  }
}

function truncate(s, n = 110) {
  if (!s) return "";
  const trimmed = s.trim();
  if (trimmed.length <= n) return trimmed;
  return trimmed.slice(0, n - 1).trimEnd() + "…";
}

export default function HorizontalTimeline({
  timeline,
  selectedSessionId,
  onSelect,
  practiceId,
}) {
  const { behaviourOf } = useAppointmentStatuses(practiceId);

  // Struck through when the appointment did not happen. Driven by the status's
  // type, so a practice's own "Cancelled by Patient" is treated the same way.
  const isSkipped = (status) => behaviourOf(status, practiceId).strikeThrough;
  // Ascending order so earliest is on the left, newest on the right.
  const sorted = useMemo(() => {
    return [...(timeline || [])].sort((a, b) => {
      const ta = a.date ? new Date(a.date).getTime() : 0;
      const tb = b.date ? new Date(b.date).getTime() : 0;
      return ta - tb;
    });
  }, [timeline]);

  // Auto-scroll to the selected marker so users always see what's selected,
  // even when the timeline overflows horizontally.
  const scrollRef = useRef(null);
  const selectedRef = useRef(null);
  useEffect(() => {
    if (selectedRef.current && scrollRef.current) {
      const cont = scrollRef.current;
      const el = selectedRef.current;
      const left = el.offsetLeft - cont.offsetWidth / 2 + el.offsetWidth / 2;
      cont.scrollTo({ left, behavior: "smooth" });
    }
  }, [selectedSessionId]);

  // Notes, files and letters share the timeline. Older memory docs have no
  // entryId or kind; those are notes keyed by sessionId.
  const idOf = (entry) => entry.entryId || entry.sessionId;
  const kindOf = (entry) => entry.kind || "NOTE";

  if (!sorted.length) {
    return (
      <div className="text-muted small py-3">No previous sessions, documents or letters yet.</div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="overflow-auto pb-2"
      // width: 0 + minWidth: 100% keeps this scroller from contributing its
      // content's intrinsic width (entries x ENTRY_MIN_WIDTH) to ancestor
      // flex/grid sizing. Without it a long timeline widened the column's
      // minimum size and flex-wrap pushed the whole content column below the
      // nav rail, which read as a blank page. The box still fills its parent
      // (minWidth: 100%) and the timeline scrolls inside it.
      style={{ scrollbarWidth: "thin", width: 0, minWidth: "100%" }}
    >
      <div
        className="position-relative"
        style={{
          height: TIMELINE_HEIGHT,
          minWidth: sorted.length * ENTRY_MIN_WIDTH,
        }}
      >
        {/* Horizontal spine */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: 0,
            right: 0,
            height: 2,
            background: "var(--acc-border-color)",
            transform: "translateY(-50%)",
          }}
        />

        {/* Markers + labels */}
        <div className="d-flex h-100" style={{ position: "relative" }}>
          {sorted.map((entry, i) => {
            const above = i % 2 === 0;
            const kind = kindOf(entry);
            // Only a booking's status can mean "did not happen".
            const skipped = kind === "NOTE" && isSkipped(entry.status);
            const isSelected = idOf(entry) === selectedSessionId;
            // Palette only: brand primary for the selected marker, the brand
            // danger red (used for negative amounts) for skipped sittings,
            // Bootstrap's secondary for the rest.
            const dotColor = isSelected
              ? "var(--acc-brand-primary)"
              : skipped
                ? "#b02a37"
                : "var(--bs-secondary)";

            const select = () => onSelect && onSelect(entry);
            const onKey = (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                select();
              }
            };

            // The whole caption is a target: the dot alone is too small to
            // hit, especially on a touch screen.
            const label = (
              <div
                className="px-2 rounded"
                role="button"
                tabIndex={0}
                onClick={select}
                onKeyDown={onKey}
                aria-label={`Open ${kind === "FILE" ? "document" : kind === "LETTER" ? "letter" : "session"} on ${formatDate(entry.date)}`}
                style={{
                  textAlign: "center",
                  opacity: skipped ? 0.65 : 1,
                  cursor: "pointer",
                  background: isSelected ? "var(--acc-row-hover-bg)" : "transparent",
                }}
              >
                <div
                  className="fw-semibold small"
                  style={{
                    color: isSelected ? "var(--acc-brand-primary)" : "var(--bs-body-color)",
                    textDecoration: skipped ? "line-through" : "none",
                  }}
                >
                  {formatDate(entry.date)}
                </div>
                <div
                  className="text-muted"
                  style={{ fontSize: "0.75rem", lineHeight: 1.3 }}
                >
                  {kind === "NOTE" && entry.status && entry.status !== "Completed" && (
                    <span className="d-block">{entry.status}</span>
                  )}
                  {kind !== "NOTE" && entry.title && (
                    <span className="d-block fw-semibold">{truncate(entry.title, 40)}</span>
                  )}
                  {truncate(entry.summary, kind === "NOTE" ? 110 : 70)}
                </div>
              </div>
            );

            const connector = (
              <div
                style={{
                  width: 2,
                  height: 18,
                  background: dotColor,
                  margin: "0 auto",
                }}
              />
            );

            return (
              <div
                key={idOf(entry)}
                ref={isSelected ? selectedRef : null}
                className="flex-fill h-100 position-relative"
                style={{ minWidth: ENTRY_MIN_WIDTH }}
              >
                {/* Top half: content shows when `above` */}
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "calc(50% - 8px)",
                  }}
                  className="d-flex flex-column justify-content-end"
                >
                  {above && (
                    <>
                      {label}
                      {connector}
                    </>
                  )}
                </div>

                {/* Marker dot: sits on the horizontal spine */}
                <button
                  type="button"
                  onClick={() => onSelect && onSelect(entry)}
                  title={`${kind === "NOTE" ? (entry.status || "Session") : (entry.title || entry.status || kind)} · ${formatDate(entry.date)}${entry.sourceType === "SYNCTO_NOTE" ? " · SyncTo" : ""}`}
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: isSelected ? 18 : 14,
                    height: isSelected ? 18 : 14,
                    borderRadius: "50%",
                    background: dotColor,
                    border: "3px solid white",
                    boxShadow: "0 0 0 2px " + dotColor,
                    cursor: "pointer",
                    padding: 0,
                    zIndex: 2,
                    transition: "all 0.15s ease",
                  }}
                  aria-label={`Select ${kind === "FILE" ? "document" : kind === "LETTER" ? "letter" : "session"} on ${formatDate(entry.date)}`}
                />

                {/* Source badge on the free side of the dot, opposite the
                    caption: microphone for a SyncTo note, a page for a
                    document from Patient Files, an envelope for a letter this
                    practice produced. A small white chip, and a second click
                    target for the entry. */}
                {(entry.sourceType === "SYNCTO_NOTE" || kind !== "NOTE") && (
                  <OverlayTrigger
                    placement={above ? "bottom" : "top"}
                    overlay={
                      <Tooltip>
                        {kind === "FILE" ? "Document from Patient Files" : kind === "LETTER" ? "Letter produced by the practice" : "SyncTo-generated note"}
                      </Tooltip>
                    }
                  >
                    <button
                      type="button"
                      onClick={select}
                      aria-label={kind === "FILE" ? "Document from Patient Files" : kind === "LETTER" ? "Letter produced by the practice" : "SyncTo-generated note"}
                      className="text-primary p-0"
                      style={{
                        position: "absolute",
                        // Caption above: chip just below the dot. Caption
                        // below: chip just above it.
                        top: above ? "calc(50% + 12px)" : "calc(50% - 32px)",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        background: "#fff",
                        border: `1px solid ${dotColor}`,
                        fontSize: "0.65rem",
                        lineHeight: 1,
                        cursor: "pointer",
                        zIndex: 3,
                      }}
                    >
                      <i className={`fa-solid ${kind === "FILE" ? "fa-file-lines" : kind === "LETTER" ? "fa-envelope" : "fa-microphone"}`} />
                    </button>
                  </OverlayTrigger>
                )}

                {/* Bottom half: content shows when `!above` */}
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "calc(50% - 8px)",
                  }}
                  className="d-flex flex-column justify-content-start"
                >
                  {!above && (
                    <>
                      {connector}
                      {label}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
